import { useCallback, useEffect, useState } from 'react';
import { PAGINATION } from '@erpgold/shared';
import { staffService, Staff, StaffFilters } from '../services/staff.service';

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export const useStaffs = (initialFilters: StaffFilters = {}) => {
  const [staffs, setStaffs] = useState<Staff[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [filters, setFilters] = useState<StaffFilters>({
    page: PAGINATION.DEFAULT_PAGE,
    limit: PAGINATION.DEFAULT_LIMIT,
    ...initialFilters,
  });
  const [meta, setMeta] = useState<PaginationMeta>({
    page: PAGINATION.DEFAULT_PAGE,
    limit: PAGINATION.DEFAULT_LIMIT,
    total: 0,
    totalPages: 0,
  });

  const fetchStaffs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await staffService.getStaffs(filters);
      setStaffs(response.data);
      setMeta(response.meta);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch staffs'));
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchStaffs();
  }, [fetchStaffs]);

  return { staffs, loading, error, meta, filters, setFilters, refetch: fetchStaffs };
};

export const useStaffDetail = (id: string) => {
  const [staff, setStaff] = useState<Staff | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchStaff = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const data = await staffService.getStaffById(id);
      setStaff(data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch staff'));
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchStaff();
  }, [fetchStaff]);

  return { staff, loading, error, refetch: fetchStaff };
};
